import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { authClient } from '../../lib/auth-client';
import { LoginRequestSchema, type LoginRequest } from '../../schemas';
import { validateForm } from '../../schemas/forms';
import { mapBetterAuthSlug, FrontendErrorCode } from '../../lib/errorCodes';

export const useLoginForm = () => {
	const [formData, setFormData] = React.useState<LoginRequest>({
		email: '',
		password: '',
		rememberMe: false
	});
	const [validationErrors, setValidationErrors] = React.useState<z.ZodError | null>(null);
	const [showPassword, setShowPassword] = React.useState(false);

	const navigate = useNavigate();
	const frontendUrl = window.location.origin;

	const handleTogglePasswordVisibility = () => {
		setShowPassword((prev) => !prev);
	};

	const handleGoogleSignIn = async () => {
		try {
			await authClient.signIn.social({
				provider: 'google',
				callbackURL: `${frontendUrl}/dashboard`,
				errorCallbackURL: `${frontendUrl}/error?code=oauth_error`
			});
		} catch (error) {
			console.error('Google sign-in error:', error);
		}
	};

	React.useEffect(() => {
		const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
		if (googleClientId) {
			authClient.oneTap({
				callbackURL: '/dashboard',
				fetchOptions: {
					onError: (ctx) => {
						console.error('Google One Tap error:', ctx.error);
					}
				},
			});
		}
	}, []);

	const loginMutation = useMutation({
		mutationFn: async (data: LoginRequest) => {
			const isEmail = data.email.includes('@');
			
			const response = isEmail 
				? await authClient.signIn.email({
					email: data.email,
					password: data.password,
					rememberMe: data.rememberMe
				})
				: await authClient.signIn.username({
					username: data.email,
					password: data.password,
					rememberMe: data.rememberMe
				});

			if (response.error) {
				const respErr = response.error as { code?: string | number; message?: string };
				const slug = typeof respErr.code === 'string' ? respErr.code : undefined;
				// If the backend already returned a 5-digit application code (e.g. our
				// rate-limit middleware sends "10009"), use it directly. Otherwise treat
				// the value as a Better Auth slug and map it; unknown slug → 90005.
				let appCode: string;
				if (slug && /^[0-9]{5}$/.test(slug)) {
					appCode = slug;
				} else if (slug) {
					appCode = mapBetterAuthSlug(slug) ?? FrontendErrorCode.UNMAPPED_SLUG;
				} else {
					appCode = FrontendErrorCode.NULL_PAYLOAD;
				}
				const err = new Error(response.error.message || 'Login failed') as Error & {
					code?: string;
				};
				err.code = appCode;
				throw err;
			}

			return response.data;
		},
		onSuccess: (data) => {
			if (data?.user) {
				navigate('/dashboard');
			}
		},
		onError: (error: Error) => {
			console.error('Login failed:', error);
		},
	});

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const validation = validateForm(LoginRequestSchema, formData);

		if (!validation.success) {
			setValidationErrors(validation.errors ?? null);
			return;
		}

		setValidationErrors(null);
		if (validation.data) {
			loginMutation.mutate(validation.data);
		}
	};

	const handleInputChange = (field: keyof LoginRequest) => (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const value = field === 'rememberMe' ? event.target.checked : event.target.value;
		setFormData((prev: LoginRequest) => ({ ...prev, [field]: value }));

		if (validationErrors) {
			setValidationErrors(null);
		}
	};

	return {
		formData,
		validationErrors,
		showPassword,
		loginMutation,
		handleTogglePasswordVisibility,
		handleGoogleSignIn,
		handleSubmit,
		handleInputChange,
	};
};
